import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import PageTypes from 'constants/pageTypes';
import {
  handleSearchSuggestionClick,
  handleSearchSuggestionForHistory,
  handlePopularSuggestionClick,
} from 'helpers/utils/search/searchSuggestion';
import {
  SearchSuggestion,
  TrendingSearch,
  ProductSuggestion,
  OtherSuggestion,
  PopularSuggestion,
} from 'types/searchSuggestion';
import SearchListing from 'shared/pages/SearchListing';
import OldSuggestions from 'layouts/SearchSuggestions/Suggestions';

import Header from './Header';
import Suggestions from './Suggestions';
import Loading from './Loading';
import Trending from './Trending';
import History from './History';
import { searchTypes } from 'constants/searchSuggestion';
import HistoryChips from './HistoryChips';
import { pageNamePrefix } from 'constants/dataLayer';
import { pushEvent } from 'helpers/utils/dataLayer';
import RecentlyViewed from './RecentlyViewed';
import { sendSingleEvent } from '@nykaa/analytics-utils/helpers/analytics';
import {
  getASLoad as getSuggestionsLoadEvent,
  getASClick as getSuggestionsClickEvent,
} from '@nykaa/analytics-utils/helpers/autoSuggestion';
import { useLocation } from 'react-router-dom';
import { getUpdatedPageType } from 'shared/helpers';
import PopularSuggestions from './PopularSuggestions';

interface SearchSuggestionProps {
  pageType: PageTypes;
  productSuggestions: ProductSuggestion[];
  otherSuggestions: OtherSuggestion[];
  searchAutoSuggestions: SearchSuggestion[];
  searchField: string;
  maxSearchHistoryCount: number;
  trendingSearchesToShow: number;
  searchSuggestionIcons: [];
  fetchTrendingSearches: () => Promise<void>;
  resetSearch: () => void;
  newScreen: boolean;
  isPopularSearchSuggestion: boolean;
  showRecentlyViewedWidget: boolean;
}

const Announcement = styled.div`
  position: absolute;
  left: -10000px;
  width: 1px;
  height: 1px;
  overflow: hidden;
`;

const SearchSuggestions = ({
  pageType,
  searchAutoSuggestions = [],
  productSuggestions = [],
  otherSuggestions = [],
  searchField = '',
  maxSearchHistoryCount,
  trendingSearchesToShow,
  searchSuggestionIcons,
  fetchTrendingSearches,
  resetSearch,
  newScreen,
  isPopularSearchSuggestion,
  showRecentlyViewedWidget,
}: SearchSuggestionProps) => {
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [announcement, setAnnouncement] = useState('');
  const { search } = useLocation();
  const updatedPageType = getUpdatedPageType(search, pageType);
  // FIXME: move the trending search logic to the Trending Search component
  useEffect(() => {
    if (trendingSearchesToShow > 0 && !isPopularSearchSuggestion) {
      setLoadingSuggestions(true);
      fetchTrendingSearches().then(() => {
        setLoadingSuggestions(false);
      });
    }

    return () => {
      resetSearch();
    };
  }, [
    fetchTrendingSearches,
    resetSearch,
    trendingSearchesToShow,
    isPopularSearchSuggestion,
  ]);

  useEffect(() => {
    SearchListing.preload();
  }, []);

  /*  Logic to send retina event,
      whenever api call made and autocomplete list changes
  */
  useEffect(() => {
    if (
      searchAutoSuggestions &&
      searchAutoSuggestions.length > 0 &&
      searchField &&
      searchField.length > 0
    ) {
      sendSingleEvent(
        getSuggestionsLoadEvent({
          searchTerm: searchField,
          autoSuggestionsList: searchAutoSuggestions,
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchAutoSuggestions]);

  // Announce suggestion count for screen readers
  useEffect(() => {
    if (searchField) {
      if (searchAutoSuggestions && searchAutoSuggestions.length === 0) {
        setAnnouncement('No suggestions present');
      } else if (
        searchField &&
        searchAutoSuggestions &&
        searchAutoSuggestions.length > 0
      ) {
        const count = searchAutoSuggestions.length;
        setAnnouncement(
          `${count} ${count === 1 ? 'suggestion' : 'suggestions'} present`
        );
      }
    } else {
      setAnnouncement('');
    }
  }, [searchAutoSuggestions, searchField]);

  const handleClick = (suggestion: SearchSuggestion, position: number) => {
    handleSearchSuggestionForHistory(suggestion, maxSearchHistoryCount);
    handleSearchSuggestionClick(
      suggestion,
      updatedPageType,
      searchField,
      position
    );
    sendSingleEvent(
      getSuggestionsClickEvent({
        searchTerm: searchField,
        clickPosition: position - 1,
        selectedItem: suggestion,
        autoSuggestionsList: searchAutoSuggestions,
      })
    );
  };

  const handleTrendingSearchClick = (
    suggestion: TrendingSearch,
    position: number
  ) => {
    const modifiedSuggestion: SearchSuggestion = {
      ...suggestion,
      id: position.toString(),
    };
    modifiedSuggestion.listItemType = searchTypes.TRENDING;
    handleSearchSuggestionForHistory(modifiedSuggestion, maxSearchHistoryCount);
    handleSearchSuggestionClick(
      modifiedSuggestion,
      updatedPageType,
      searchField,
      position
    );
  };

  const handlePopularSearchClick = (
    suggestion: PopularSuggestion,
    position: number
  ) => {
    handlePopularSuggestionClick(suggestion, updatedPageType, position);
  };

  useEffect(() => {
    pushEvent('isSearchQueryPage', {
      pageName: pageNamePrefix.SEARCH_QUERY,
    });
  }, []);

  return (
    <>
      {/* Visually hidden aria-live region for screen reader announcements */}
      <Announcement
        role="status"
        aria-live="polite"
        aria-atomic="true"
      >
        {announcement}
      </Announcement>
      <Header />
      {searchField &&
        (newScreen ? (
          <>
            <Suggestions
              handleClick={handleClick}
              suggestions={searchAutoSuggestions}
              searchField={searchField}
              searchSuggestionIcons={searchSuggestionIcons}
            />
          </>
        ) : (
          <>
            <OldSuggestions
              handleClick={handleClick}
              searchField={searchField}
              suggestions={otherSuggestions}
            />
            <OldSuggestions
              handleClick={handleClick}
              suggestions={productSuggestions}
              searchField={searchField}
              isOfTypeProduct
              title="Top Products"
            />
          </>
        ))}
      {!searchField &&
        (!isPopularSearchSuggestion ? (
          <>
            <History
              handleClick={handleClick}
              searchSuggestionIcons={searchSuggestionIcons}
            />
            {loadingSuggestions && <Loading items={5} />}
            <Trending handleClick={handleTrendingSearchClick} />
          </>
        ) : (
          <>
            <HistoryChips handleClick={handleClick} />
            {showRecentlyViewedWidget && <RecentlyViewed />}
            <PopularSuggestions
              handlePopularSearchClick={handlePopularSearchClick}
            />
          </>
        ))}
    </>
  );
};

export default SearchSuggestions;
